// Redaction vocabulary + the secret-shape question (arc 101 slice 06).
//
// Two things live here:
//   1. `REDACTION_PATTERNS` — the platform's ONE set of secret shapes. It used to
//      live in `@developerz/audit` (still its public export: re-exported verbatim
//      from `packages/audit/src/redact.ts`, so every existing import path and the
//      `[REDACTED:<tag>]` sentinel are unchanged). It moved here because the sinks
//      are no longer all downstream of audit — a store that must REFUSE a secret
//      before it is written may sit in a package that cannot depend on `audit`
//      (`@developerz/mcp`'s `config` verb imports domain only), and a second copy
//      of the vendor shapes is exactly the drift this const's contract forbids.
//   2. `secretShapesIn*` — "is this value credential-shaped?", the question a
//      PLAINTEXT store asks at its write boundary. Same patterns, opposite verb:
//      a sink rewrites what it found, a plaintext store refuses the write.
//
// No I/O, no rewriting: these functions only ever answer with tag names, never
// with matched text — a caller can name the shape in a refusal without echoing
// the secret it refused.

// A path segment that reads as a WORD: a whole `/`-delimited segment of an
// optional capital plus 3+ lower-case letters (`src`, `Scripts`), or a 4+ digit
// number (`3000`, `2026`). Real paths and URLs carry one almost always (the TLD
// or first directory at least); a random base64 segment almost never does.
const PATH_WORD = String.raw`(?:[A-Z]?[a-z]{3,}|\d{4,})`;

/** Base64url run (`[A-Za-z0-9_-]`): the shape `high_entropy` has always caught. */
const BASE64URL_RUN = String.raw`(?<![A-Za-z0-9_-])(?=[A-Za-z0-9_-]*[a-z])(?=[A-Za-z0-9_-]*[A-Z])(?=[A-Za-z0-9_-]*\d)[A-Za-z0-9_-]{32,}`;

/** Standard-base64 run: carries `+` or `/`, may end in `=` padding, and is not a path. */
const BASE64_RUN = [
  // Boundaries on both sides, over the union of both alphabets: a run touching
  // `_` or `-` is base64url's (or a hyphenated path's), never this branch's.
  // `=` is excluded from the left boundary so `KEY=<secret>` still starts a run.
  String.raw`(?<![A-Za-z0-9_+\/-])`,
  String.raw`(?=[A-Za-z0-9]*[+\/])`, // a `+` or `/` inside, else it is the branch below's
  // Not a path: a run whose only separator is `/` (no `+`, no trailing `=`)
  // AND holds a whole PATH_WORD segment. Two flat scans, each linear.
  String.raw`(?!(?=[A-Za-z0-9\/]*(?![A-Za-z0-9+\/=]))[A-Za-z0-9\/]*?(?<![A-Za-z0-9])${PATH_WORD}(?![A-Za-z0-9]))`,
  String.raw`(?=[A-Za-z0-9+\/]*[a-z])(?=[A-Za-z0-9+\/]*[A-Z])(?=[A-Za-z0-9+\/]*\d)`,
  String.raw`[A-Za-z0-9+\/]{32,}={0,2}(?![A-Za-z0-9_+\/=-])`,
].join('');

/**
 * The generic high-entropy fallback: secrets no named pattern caught (custom BYOK
 * auth tokens, opaque bearers, AWS secret access keys, base64 KEKs, PEM lines).
 * A 32+ char run mixing lower + upper + digit, in either of two alphabets:
 *
 *   - **base64url** `[A-Za-z0-9_-]`: the original rule, unchanged.
 *   - **standard base64** `[A-Za-z0-9+/]` with `=` padding, claimed WHOLE, so a
 *     `+` or `/` no longer cuts a secret into runs under the floor (#3948).
 *
 * The mixed-class gate is a cheap entropy proxy that spares single-case hashes
 * (git SHAs, hex digests) and UUIDs. The `/` in standard base64 is also the path
 * separator, so the rule is: **a run whose only separator is `/` is a path when
 * any whole segment is a PATH_WORD; a `+` or `=` padding makes it base64
 * regardless.** The trade, measured over 200k random keys:
 *   - missed: a slash-only secret with a word-shaped segment (`…/tmpl/…`), under
 *     1% of slash-only 40-char keys, which are about a quarter of all of them.
 *     Pinned by a test.
 *   - collateral (redacted, and refused by the write guards): data-URI payloads,
 *     unspaced `+` joins of mixed identifiers (`basePrice+shipping2Cost+…`),
 *     form-encoded query values carrying `+`, and a slash-only mixed-case path
 *     with no word segment at all.
 * Every lookbehind pins an attempt to a run boundary, which keeps the scan
 * linear on large payloads.
 */
const HIGH_ENTROPY = new RegExp(`${BASE64_RUN}|${BASE64URL_RUN}`, 'g');

/**
 * The single, canonical redaction pattern set for the whole platform. Every
 * secret sink imports THIS const — the audit writer (`@developerz/audit`), the
 * model system prompt (`@developerz/agent`), and the runner subprocess mirror
 * (`apps/runner`) — so vendor coverage can never drift between sinks. The
 * write-side guards (`secretShapesInJson`) derive from the same array, so a new
 * vendor shape hardens the refusals in the same commit it hardens the sinks.
 *
 * Order is load-bearing: patterns run top-to-bottom and each replaces its
 * matches with `[REDACTED:<tag>]`, so the *specific* vendor shapes must
 * precede the broad ones:
 *   - Anthropic (`sk-ant-…`) is a subset of the broadened OpenAI shape
 *     (`sk-…`), so it must match first to keep its own tag.
 *   - The generic high-entropy fallback runs LAST, catching only what no
 *     named pattern already claimed (e.g. non-`sk-ant` BYOK auth tokens).
 *
 * The tag lands inside `[REDACTED:<tag>]` and on the audit row's `redaction`
 * column so the maintainer knows *why* a field is missing.
 */
export const REDACTION_PATTERNS: ReadonlyArray<{ tag: string; pattern: RegExp }> = [
  // GitHub classic / scoped PATs
  { tag: 'github_token', pattern: /(?:ghp|gho|ghu|ghs|ghr)_[A-Za-z0-9]{36,}/g },
  // GitHub fine-grained PATs
  { tag: 'github_pat', pattern: /github_pat_[A-Za-z0-9_]+/g },
  // Slack tokens, and the incoming-webhook URL: its secret is a bare 24-char
  // segment no other shape reaches, so the whole `hooks.slack.com/services/…`
  // tail is the credential. Same tag: a webhook URL IS a Slack token.
  {
    tag: 'slack_token',
    pattern:
      /xox[abprs]-[A-Za-z0-9-]+|hooks\.slack\.com\/services\/T[A-Z0-9]+\/B[A-Z0-9]+\/[A-Za-z0-9]+/g,
  },
  // Anthropic keys (`sk-ant-api03-…`). MUST precede the broadened OpenAI
  // pattern below — otherwise `sk-…` swallows it under the wrong tag.
  { tag: 'anthropic_key', pattern: /(?<![A-Za-z0-9])sk-ant-[A-Za-z0-9_-]{20,}/g },
  // OpenAI-shape keys. Broadened from `[A-Za-z0-9]{32,}` to allow `_`/`-` with
  // a 20-char floor so project/service keys (`sk-proj-…`, `sk-svcacct-…`) match.
  // The left boundary prevents ordinary identifiers ending in `tsk-…` (fleet
  // task branches) from being clipped at the embedded `sk-` substring (#679).
  { tag: 'openai_key', pattern: /(?<![A-Za-z0-9])sk-[A-Za-z0-9_-]{20,}/g },
  // Google / Gemini API keys — fixed 39-char `AIza`-prefixed shape.
  { tag: 'gemini_key', pattern: /AIza[0-9A-Za-z_-]{35}/g },
  // AWS access key ids
  { tag: 'aws_access_key', pattern: /AKIA[0-9A-Z]{16}/g },
  // Email addresses (basic form — RFC-perfection is out of scope for redaction).
  // The left edge is a run-start lookbehind, not `\b`: `\b` also fires after
  // every `.`, `+` or `-` inside the local-part class, so a long `a.a.a…` run with
  // no `@` restarted the scan at each one (quadratic, 3.6 s on 64 KB).
  {
    tag: 'email',
    pattern: /(?<![A-Za-z0-9._%+-])[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
  },
  // NATS NKEY seeds (the `.nats.creds` fleet credential): exactly 58 chars of
  // upper-case base32, `S` + a type letter (U user, A account, O operator, N server,
  // C cluster, X curve, P private). Single-case, so `high_entropy`'s mixed-class gate
  // spares it; it needs its own shape (row 09-30). Bounded by base32-class
  // lookarounds, not `\b`, so a seed glued to `_` or punctuation still matches.
  // KNOWN MISS, chosen: a seed glued to an upper-case letter or a digit 2-7 is NOT
  // redacted. Without the lookarounds any long upper-case run (prompt prose, a row
  // of `S`s) gets sliced into a seed-shaped match and mangled.
  { tag: 'nats_nkey_seed', pattern: /(?<![A-Z2-7])S[UAONCXP][A-Z2-7]{56}(?![A-Z2-7])/g },
  // Generic high-entropy fallback for secrets no named pattern caught. Runs LAST
  // so named tags win wherever they apply. See `HIGH_ENTROPY` for the rule.
  { tag: 'high_entropy', pattern: HIGH_ENTROPY },
];

/**
 * Tags a sink MASKS but a plaintext store may still hold. `email` alone: an
 * address is PII an audit row scrubs, not a credential — a maintainer files
 * `oncall_contact` as a prompt var on purpose, and refusing it would only teach
 * them to obfuscate the address while the real secrets kept flowing. Adding a
 * tag here weakens every write guard, so the set is deliberately one entry long
 * and pinned by a test.
 */
export const SECRET_SHAPE_EXEMPT_TAGS: ReadonlySet<string> = new Set(['email']);

/**
 * The credential subset of {@link REDACTION_PATTERNS} — **derived, never
 * declared**, so a vendor shape added for the sinks also hardens every write
 * guard, and neither list can be extended without the other.
 */
export const SECRET_SHAPE_PATTERNS: ReadonlyArray<{ tag: string; pattern: RegExp }> =
  REDACTION_PATTERNS.filter((p) => !SECRET_SHAPE_EXEMPT_TAGS.has(p.tag));

/**
 * Which of `patterns` match somewhere in `text`, as sorted tag names — a
 * FLAG-only scan: nothing is rewritten and no matched text is returned.
 *
 * Each match is masked as we go so a specific tag claims its span before a
 * broader pattern sees it: one Anthropic key answers `anthropic_key` alone, not
 * also `openai_key` + `high_entropy` (the `sk-…` shape is a superset). That is
 * the same sequential semantics the rewriting scrubber has, which is why
 * `@developerz/audit`'s `scanReasoning` is a thin wrapper over this.
 */
export function matchedRedactionTags(
  text: string,
  patterns: ReadonlyArray<{ tag: string; pattern: RegExp }> = REDACTION_PATTERNS,
): string[] {
  const hits = new Set<string>();
  let residue = text;
  for (const { tag, pattern } of patterns) {
    // The patterns are shared `/g` module state: `replace`/`test` advance
    // `lastIndex`, so a stale index from an earlier call would start mid-string
    // and miss a match. Reset before every use.
    pattern.lastIndex = 0;
    let matched = false;
    residue = residue.replace(pattern, () => {
      matched = true;
      return ' ';
    });
    if (matched) hits.add(tag);
  }
  return [...hits].sort();
}

/** Credential shapes present in `text` (emails excluded — see {@link SECRET_SHAPE_EXEMPT_TAGS}). */
export function secretShapesInText(text: string): string[] {
  return matchedRedactionTags(text, SECRET_SHAPE_PATTERNS);
}

/**
 * Credential shapes anywhere inside a JSON value — the question a plaintext
 * jsonb store asks before accepting a write. Scans the SERIALIZED form, so a
 * secret nested in an object/array (or used as a key) is found wherever it sits;
 * JSON's own delimiters (`"`, `,`, `:`, `{`, `}`) break every character run, so
 * two innocent leaves can never splice into a false match.
 *
 * A value that does not serialize (`undefined`, a cycle) answers `[]`: it cannot
 * be STORED as jsonb either, so refusing it is the caller's separate, unrelated
 * guard rather than a secret-shape verdict.
 */
export function secretShapesInJson(value: unknown): string[] {
  let serialized: string | undefined;
  try {
    serialized = JSON.stringify(value);
  } catch {
    return [];
  }
  return serialized === undefined ? [] : secretShapesInText(serialized);
}

/**
 * ONE `[REDACTED:<tag>]` sentinel, as every scrub writes it: a pattern tag (`high_entropy`),
 * an exact-match env var name (`DZ_BACKEND_TOKEN`) or a derived name
 * (`DZ_LOG_SINK_URL:password`). CAPTURING, so `text.split(REDACTION_SENTINEL_RE)` keeps
 * each sentinel at the odd indices — the shape a clamp that must not cut one needs.
 */
export const REDACTION_SENTINEL_RE = /(\[REDACTED:[A-Za-z0-9_:-]+\])/u;

const SENTINEL_ONLY_RE = new RegExp(`^(?:\\s*${REDACTION_SENTINEL_RE.source})+\\s*$`, 'u');

/**
 * TRUE when a scrub left NOTHING of `text` but {@link REDACTION_SENTINEL_RE} sentinels —
 * the field was, by the table's own rule, wholly secret-shaped. A writer that MINTS from a
 * scrubbed field (a scout group's task title) refuses it with a named reason instead of
 * storing the placeholder as the field's value. Blank text is not "only sentinels".
 */
export function textIsOnlyRedactionSentinels(text: string): boolean {
  return SENTINEL_ONLY_RE.test(text);
}
